import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User } from 'lucide-react';
import { getAvatarSrc } from '../lib/avatarUtils';

interface SignUpButtonProps {
  onOpenConsole: () => void;
  pinCount?: number;
  onOpenList?: () => void;
}

const BADGE_VISIBLE_MS = 4000;

const SignUpButton: React.FC<SignUpButtonProps> = ({ onOpenConsole, pinCount = 0, onOpenList }) => {
  const { isAuthenticated, backendUser } = useAuth();
  const avatarFilename = backendUser?.user_preferences?.avatar ?? '';
  const avatarSrc = avatarFilename ? getAvatarSrc(avatarFilename) : '';
  const [showCount, setShowCount] = useState(false);
  const prevPinCountRef = useRef(pinCount);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prev = prevPinCountRef.current;
    prevPinCountRef.current = pinCount;

    if (pinCount === 0) {
      // All pins removed — hide immediately
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      setShowCount(false);
      return;
    }

    if (pinCount > prev) {
      // New pin added — show badge and (re)start dismiss timer
      setShowCount(true);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => setShowCount(false), BADGE_VISIBLE_MS);
    }
  }, [pinCount]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const handleClick = () => {
    if (showCount && onOpenList) {
      onOpenList();
    } else {
      onOpenConsole();
    }
  };

  return (
    <button
      onClick={handleClick}
      className="ceen-button"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        backgroundColor: showCount
          ? 'var(--color-primary)'
          : isAuthenticated && avatarSrc
            ? '#afdcfd'
            : isAuthenticated
              ? 'var(--color-neutral)'
              : 'var(--color-primary)',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        zIndex: 9999,
        touchAction: 'manipulation',
        minHeight: '44px',
        minWidth: '44px',
        WebkitTapHighlightColor: 'rgba(245, 158, 11, 0.3)',
        transition: 'background-color 0.2s ease-in-out',
        overflow: 'hidden',
      }}
    >
      {/* Count badge — fades in/out */}
      <span
        style={{
          position: 'absolute',
          color: 'white',
          fontSize: pinCount > 9 ? '0.75rem' : '0.9rem',
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: pinCount > 9 ? '0' : '1px',
          opacity: showCount ? 1 : 0,
          transform: showCount ? 'scale(1)' : 'scale(0.7)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          pointerEvents: 'none',
        }}
      >
        {pinCount > 20 ? '20+' : pinCount}
      </span>

      {/* Avatar / user icon — fades in when badge hides */}
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          opacity: showCount ? 0 : 1,
          transform: showCount ? 'scale(0.7)' : 'scale(1)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          pointerEvents: 'none',
          overflow: 'hidden',
          borderRadius: '50%',
          backgroundColor: isAuthenticated && avatarSrc ? '#afdcfd' : 'transparent',
        }}
      >
        {isAuthenticated && avatarSrc ? (
          <img
            src={avatarSrc}
            alt="avatar"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
            }}
          />
        ) : (
          <User size={isAuthenticated ? 22 : 18} color={isAuthenticated ? 'black' : 'white'} />
        )}
      </span>
    </button>
  );
};

export default SignUpButton;
